import type { VirtualElement } from '@popperjs/core'
import { Next, Play as PlayIcon, Prev, Stop as StopIcon } from '@tunasong/icons'
import type { AudioPosition } from '@tunasong/models'
import { Popup, Typography, useHotkey, Fab} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, HTMLAttributes } from 'react'
import { useEffect, useState } from 'react'
import { useTimelineInteraction } from '../hooks/index.js'
import { trackMouse } from '../lib/index.js'
import { PlayerProgress } from '../progress.js'
import { useGlobalPlayer } from './global-player.hook.js'
import styles from './player-controls.module.css'

export interface PlayerControlsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect' | 'ref'> {
  // Name of the audio channel
  name?: string
  title?: string
  /** Capture keyboard events to control start/stop, next, prev */
  keyboardControl?: boolean
  // onSelect(idx: number): void
  onSeek?(pos: number | AudioPosition): void
}

export const PlayerControls: FC<PlayerControlsProps> = props => {
  const { title, onSeek, keyboardControl = false, ...restProps } = props

  const { controls, playlist, isPlaying, progressLabel, duration, playerRef } = useGlobalPlayer()

  const [popupEl, setPopupEl] = useState<VirtualElement | null>(null)

  /** Track user interaction */
  const { position, pagePosition, timelineRef } = useTimelineInteraction({
    duration,
    onSeek,
  })

  useEffect(() => {
    setPopupEl(pagePosition ? trackMouse(pagePosition) : null)
  }, [pagePosition])

  const [ref, setRef] = useState<HTMLDivElement | null>(null)

  useHotkey({ hotkey: 'ArrowLeft', handler: playlist.prev, disabled: !keyboardControl, el: ref })
  useHotkey({ hotkey: 'ArrowRight', handler: playlist.next, disabled: !keyboardControl, el: ref })
  useHotkey({ hotkey: 'Space', handler: controls.togglePlay, disabled: !keyboardControl, el: ref })

  return (
    <>
      <div
        {...restProps}
        ref={setRef}
        className={cn(styles.controls, restProps.className)}
        style={restProps.style}
      >
        <div className={styles.buttons}>
          <Fab color="primary" disabled={!playlist.prev} size={'medium'} title={title} onClick={playlist.prev}>
            <Prev />
          </Fab>
          <Fab
            size={'medium'}
            title={title}
            disabled={!playlist.active}
            onClick={controls.togglePlay}
            color="secondary"
          >
            {isPlaying ? <StopIcon /> : <PlayIcon />}
          </Fab>
          <Fab color="primary" disabled={!playlist.next} size={'medium'} title={title} onClick={playlist.next}>
            <Next />
          </Fab>
        </div>

        <PlayerProgress ref={timelineRef} el={playerRef} />

        <div className={styles.info}>
          <Typography variant="caption">{playlist.active?.media?.name}</Typography>
          <div className={styles.infoSpacer} />
          <Typography variant="caption">{progressLabel}</Typography>
        </div>
      </div>
      <Popup open={Boolean(popupEl)} anchorEl={popupEl} placement="right-end">
        <Typography variant="caption">{position?.label}</Typography>
      </Popup>
    </>
  )
}

export default PlayerControls
